<script>
  import Icon from "./Icon.vue";

  export default {
    name: "Paginator",
    components: {Icon},
    props: {
      page: {
        type: Number,
        required: true
      },
      totalPages: {
        type: Number,
        required: true
      },
      totalRecords: {
        type: Number,
        required: false,
        default: 0
      },
      recordsShowing: {
        type: Number,
        required: false,
        default: 0
      },
      recordsPerPage: {
        type: Number,
        required: false,
        default: 0
      }
    },
    emits: ['move-to-page', 'next', 'prev'],
    computed: {
      nextButton() {
        return this.page < this.totalPages;
      },
      prevButton() {
        return this.page > 1;
      },
      pageNo() {
        if(this.recordsShowing && this.totalRecords && this.recordsPerPage){
          let start = (this.recordsPerPage * (this.page -1)) + 1;
          let end = start + this.recordsShowing -1;
          return `${start} - ${end}`;
        }
        return false;
      },
      displayedPages() {
        const pages = [];
        const maxDisplay = 15; // Number of pages to start truncating page numbers at

        if (this.totalPages <= maxDisplay) {
          for (let i = 1; i <= this.totalPages; i++) {
            pages.push(i);
          }
        } else {
          // Always show first page
          pages.push(1);

          // Calculate the range of middle pages
          const start = Math.max(2, this.page - 2);
          const end = Math.min(this.totalPages - 1, this.page + 2);

          // Add middle pages
          for (let i = start; i <= end; i++) {
            pages.push(i);
          }

          // Always show last page
          pages.push(this.totalPages);

          // Add ellipsis if needed
          if (start > 2) {
            pages.splice(1, 0, '...'); // Add ellipsis after the first page
          }
          if (end < this.totalPages - 1) {
            pages.splice(pages.length - 1, 0, '...'); // Add ellipsis before the last page
          }
        }
        return pages;
      },

    },
    methods: {
      next(){
        this.$emit('next');
      },
      prev(){
        this.$emit('prev');
      },
      moveTo(page){
        if (page !== '...') {
          this.$emit('move-to-page', page);
        }
      },
      cellClasses(pageNo){
        let classes = "flex items-center justify-center px-3 h-8 leading-tight border border-slate-300 border-collapse";

        if(pageNo !== '...') {
          classes += " cursor-pointer hover:text-default_primary_dark hover:bg-default_primary_lightest";
        }
        if(pageNo === this.page){
          classes += " text-default_primary_dark bg-default_primary_lightest ";
        }
        if(pageNo === 1 && !this.prevButton){
          classes += " rounded-s-lg ";
        }
        if(pageNo === this.totalPages && !this.nextButton){
          classes += " rounded-e-lg";
        }
        return classes;
      }
    }
  };
</script>

<template>
  <!-- Return nothing if there's only one page  -->
  <div v-if="totalPages === 1" />
  <div
    v-else
    class="flex flex-col items-center"
  >
    <nav>
      <ul class="flex flex-row md:flex-row items-center -space-x-px h-8 text-sm">
        <li v-if="prevButton">
          <a
            class="paginator-btn prev"
            @click="prev"
          >
            <span class="sr-only">Previous</span>
            <icon
              icon="chevron-left"
              color="#08602d"
              size="14px"
            />
          </a>
        </li>

        <li
          v-for="p in displayedPages"
          :key="p"
        >
          <a
            :class="cellClasses(p)"
            @click="moveTo(p)"
          >
            {{ p }}
          </a>
        </li>


        <li v-if="nextButton">
          <a
            class="paginator-btn next"
            @click="next"
          >
            <span class="sr-only">Next</span>
            <icon
              icon="chevron-right"
              color="#08602d"
              size="14px"
            />
          </a>
        </li>
      </ul>
    </nav>
    <div
      v-if="pageNo"
      class="text-sm mt-2"
    >
      {{ pageNo }} of {{ totalRecords }}
    </div>
  </div>
</template>

<style scoped lang="scss">
  .paginator-btn {
    @apply flex;
    @apply items-center;
    @apply justify-center;
    @apply px-3;
    @apply h-8;
    @apply leading-tight;
    @apply bg-white;
    @apply border;
    @apply border-gray-300;
    @apply hover:text-default_primary_dark;
    @apply hover:bg-default_primary_lightest;

    @apply cursor-pointer;


    &.next {
      @apply rounded-e-lg;
    }

    &.prev {
      @apply rounded-l-lg;

    }
  }

</style>
